import { type FC, useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

// config
import { SHELL } from 'config/setupTheme';

// common
import MyLink from 'common/components/MyLink';
import NewPagination from 'common/components/NewPagination';
import NotFoundBox from 'common/components/NotFoundBox';
import useIsParent from 'common/hook/useIsParent';
import { ROUTES_PATH } from 'layout/constants';

// screen
import { formatUTC } from 'modules/map/components/pdf-print-form/utils';
import type { FoodTraceabilityRow } from 'modules/map/components/food-traceability/types';

interface Props {
  rows: FoodTraceabilityRow[];
  customerOperationCode: string;
  initialPage?: number;
  initialRowsPerPage?: number;
}

const formatNumber = (value: number | undefined, fractionDigits = 0) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '';
  return (
    <FormattedNumber
      value={value}
      minimumFractionDigits={fractionDigits}
      maximumFractionDigits={fractionDigits}
    />
  );
};

/** Cùng ngày ăn + cùng tên món thì gộp. Tên trống thì mỗi dòng một ô, không gộp nhầm. */
const dishGroupKey = (row: FoodTraceabilityRow) => {
  const name = row.dish_display_name?.trim() ?? '';
  if (!name) return null;
  return `${row.menu_date ?? ''}\0${name}`;
};

/**
 * Số dòng `rowSpan` của cột Tên món ăn, tính trên đúng các dòng đang hiện (một trang).
 * 0 = dòng này nằm trong ô đã gộp ở dòng đầu nhóm, không render thêm ô.
 */
const dishGroupSpans = (rows: FoodTraceabilityRow[]) => {
  const spans = new Array<number>(rows.length).fill(0);
  let index = 0;
  while (index < rows.length) {
    const key = dishGroupKey(rows[index]);
    if (!key) {
      spans[index] = 1;
      index += 1;
      continue;
    }
    let end = index + 1;
    while (end < rows.length && dishGroupKey(rows[end]) === key) end += 1;
    spans[index] = end - index;
    index = end;
  }
  return spans;
};

/** Nét 1px dưới dòng cuối mỗi món, cùng độ dày kẻ dọc, đậm hơn một chút để thấy món này hết. */
const groupCellSx = (isEnd: boolean) => (theme: Theme): SystemStyleObject<Theme> =>
  isEnd
    ? {
        borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[400] : SHELL.sidebar}`,
      }
    : {};

const FoodTraceabilityTable: FC<Props> = ({
  rows,
  customerOperationCode,
  initialPage,
  initialRowsPerPage,
}) => {
  const [page, setPage] = useState(initialPage ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage ?? 20);
  const isParent = useIsParent();

  const total = rows.length;
  const start = (page - 1) * rowsPerPage;
  const pageRows = rows.slice(start, start + rowsPerPage);
  const dishSpans = dishGroupSpans(pageRows);
  const isDishGroupEnd = (index: number) => {
    const next = rows[start + index + 1];
    if (!next) return true;
    const key = dishGroupKey(pageRows[index]);
    return !key || key !== dishGroupKey(next);
  };

  const buildSupplierHref = (supplierOperationCode?: string) => {
    if (!supplierOperationCode) return undefined;
    return `/portal/${ROUTES_PATH.supplier.index}/${ROUTES_PATH.supplier.detail(
      'thuc-pham-che-bien',
      customerOperationCode,
      supplierOperationCode
    )}`;
  };

  if (!total) {
    return <NotFoundBox />;
  }

  return (
    <Box className="flex flex-col flex-1 overflow-hidden">
      <TableContainer
        sx={(theme) => ({
          flex: 1,
          '& td': {
            textAlign: 'center',
            padding: 1,
          },
          '& td:not(:last-of-type)': {
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        })}
      >
        <Table size="small">
          <TableHead
            sx={(theme) => ({
              position: 'sticky',
              top: 0,
              zIndex: 1,
              // Cùng kiểu tiêu đề bảng danh sách (MuiTable): nền xám, chữ đậm, căn giữa, kẻ dọc.
              '& th': {
                bgcolor:
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.grey[100],
                color: theme.palette.text.primary,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: 1.45,
                whiteSpace: 'pre-wrap',
                textAlign: 'center',
                verticalAlign: 'middle',
                padding: '14px 8px',
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderRight: `1px solid ${theme.palette.divider}`,
                '&:last-of-type': {
                  borderRight: 'none',
                },
              },
            })}
          >
            <TableRow>
              <TableCell style={{ width: 60 }}>
                <FormattedMessage id="stt" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.actualEatingDate" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.dishName" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.ingredientName" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.inputQuantity" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.unit" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="map.foodTraceability.supplier" />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row, index) => {
              const dishSpan = dishSpans[index];
              const cellSx = groupCellSx(isDishGroupEnd(index));
              return (
                <TableRow key={row.id}>
                  <TableCell sx={cellSx}>{row.stt}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.menu_date ? formatUTC(row.menu_date, 'L') : ''}
                  </TableCell>
                  {dishSpan > 0 && (
                    <TableCell
                      rowSpan={dishSpan}
                      sx={[
                        groupCellSx(isDishGroupEnd(index + dishSpan - 1)),
                        { verticalAlign: 'middle', fontWeight: 600 },
                      ]}
                    >
                      {row.dish_display_name || ''}
                    </TableCell>
                  )}
                  <TableCell sx={cellSx}>{row.selected_warehouse_food_name || ''}</TableCell>
                  <TableCell sx={[cellSx, { textAlign: 'right !important' }]}>
                    {formatNumber(row.standard_quantity, 3)}
                  </TableCell>
                  <TableCell sx={cellSx}>{row.unit || ''}</TableCell>
                  <TableCell sx={cellSx}>
                    {row.supplier_name ? (
                      (() => {
                        // Phụ huynh: bỏ link NCC (docx §4) -> href undefined => render text thường.
                        const href = isParent
                          ? undefined
                          : buildSupplierHref(row.supplier_operation_code);
                        return href ? (
                          <MyLink
                            to={{ pathname: href }}
                            state={{
                              from: 'foodTraceability',
                              focusOrg: customerOperationCode,
                              tablePage: page,
                              tableRowsPerPage: rowsPerPage,
                            }}
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            {row.supplier_name}
                          </MyLink>
                        ) : (
                          row.supplier_name
                        );
                      })()
                    ) : (
                      ''
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <NewPagination
        page={page}
        rowsPerPage={rowsPerPage}
        count={total}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(Number(value));
          setPage(1);
        }}
      />
    </Box>
  );
};

export default FoodTraceabilityTable;
